import axios from 'axios';
import { API_URL } from './common';
import { AvailableTime, CreateAvailableTime } from '../models/availableTime';

// バックエンドから返される利用可能時間の辞書型の定義
export interface AvailableTimesResponse {
  [date: string]: AvailableTime[]; // "2023-09-15T00:00:00": [{id: 1, date: "...", start_time: "10:00"}, ...]
}

// より詳細な時間情報が必要な場合の型定義（オプション）
export interface DetailedAvailableTime {
  time: string;
  isBooked?: boolean;
  capacity?: number;
  remainingSlots?: number;
}

export interface DetailedAvailableTimesResponse {
  [date: string]: DetailedAvailableTime[];
}

const createAvailableTime = async (
  data: CreateAvailableTime
): Promise<AvailableTime> => {
  try {
    const response = await axios.post(
      `${API_URL}/api/v1/available_time/create`,
      data
    );
    return response.data;
  } catch (error: any) {
    console.error('Get user error:', error.response?.data || error.message);
    throw error;
  }
};

const getAvailableTimes = async (
  year: number,
  month: number
): Promise<AvailableTimesResponse> => {
  try {
    const response = await axios.get(`${API_URL}/api/v1/available_time/all`, {
      params: { year, month },
    });
    return response.data;
  } catch (error: any) {
    console.error(
      'Get availableTimes error:',
      error.response?.data || error.message
    );
    throw error;
  }
};

const updateAvailableTime = async (
  id: number,
  data: AvailableTime
): Promise<AvailableTime> => {
  try {
    const response = await axios.put(
      `${API_URL}/api/v1/available_time/${id}/update`,
      data
    );
    return response.data;
  } catch (error: any) {
    console.error(
      'Update availableTime error:',
      error.response?.data || error.message
    );
    throw error;
  }
};

const deleteAvailableTime = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/api/v1/available_time/${id}/delete`);
  } catch (error: any) {
    console.error(
      'Delete availableTime error:',
      error.response?.data || error.message
    );
    throw error;
  }
};

const deleteAvailableTimeByDateTime = async (
  date: string,
  time: string
): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/api/v1/available_time/delete`, {
      params: { date: date, time: time },
    });
  } catch (error: any) {
    console.error(
      'Delete availableTime by date/time error:',
      error.response?.data || error.message
    );
    throw error;
  }
};

// ヘルパー関数：特定の日付の利用可能時間を取得
export const getAvailableTimesForDate = (
  availableTimes: AvailableTimesResponse,
  date: string
): string[] => {
  const times = availableTimes[date] || [];
  return times.map(time => time.start_time);
};

// ヘルパー関数：特定の日付の利用可能時間オブジェクトを取得
export const getAvailableTimeObjectsForDate = (
  availableTimes: AvailableTimesResponse,
  date: string
): AvailableTime[] => {
  return availableTimes[date] || [];
};

// ヘルパー関数：Date オブジェクトから適切なキー形式に変換（ローカル時間を使用）
export const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00`;
};

// ヘルパー関数：利用可能な日付一覧を取得
export const getAvailableDates = (
  availableTimes: AvailableTimesResponse
): string[] => {
  return Object.keys(availableTimes);
};

const availableTimeService = {
  createAvailableTime,
  getAvailableTimes,
  updateAvailableTime,
  deleteAvailableTime,
  deleteAvailableTimeByDateTime,
};

export default availableTimeService;
